import {
  useCallback, useEffect, useRef, useState,
} from 'react'

import {
  PREPARE_STATUS,
  requestAcquiredModelAsset,
} from 'Pods/LazyLoaded/ThreeDModelPicker/Api/prepareAcquiredModelAsset'
import { buildPreparedAcquiredModelQuery } from 'Pods/Shared/Utils/preparedAcquiredModelQuery'

export const buildEditWithAiHref = (itemSlug, preparedAsset = null) => {
  const path = `/ai-3d-model-generator/3d-models/${encodeURIComponent(itemSlug)}`

  return preparedAsset ? `${path}${buildPreparedAcquiredModelQuery(preparedAsset)}` : path
}

const isPlainLeftClick = event => (
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
)

// Prepares the generator asset here, asking which file to open when the model only ships
// inside archives, so AI Studio opens straight on the chosen file. Any failure falls back
// to the plain studio link, which runs the same flow and reports the error there.
export default function useEditWithAi(itemSlug) {
  const [candidates, setCandidates] = useState(null)
  const [isPreparing, setIsPreparing] = useState(false)
  const isPreparingRef = useRef(false)
  // Bumped when the choice is closed or the row unmounts, so a request still in flight
  // can no longer leave the page or update state.
  const requestIdRef = useRef(0)

  useEffect(() => () => {
    requestIdRef.current += 1
  }, [])

  const openStudio = useCallback((preparedAsset) => {
    window.location.assign(buildEditWithAiHref(itemSlug, preparedAsset))
  }, [itemSlug])

  const prepare = useCallback(async (archiveFile) => {
    if (isPreparingRef.current) return
    isPreparingRef.current = true
    setIsPreparing(true)
    requestIdRef.current += 1
    const requestId = requestIdRef.current
    const isStale = () => requestIdRef.current !== requestId

    try {
      const result = await requestAcquiredModelAsset(itemSlug, archiveFile)
      if (isStale()) return
      if (result.status === PREPARE_STATUS.CHOOSE_FILE) {
        isPreparingRef.current = false
        setIsPreparing(false)
        setCandidates(result.candidates)
        return
      }

      openStudio({
        assetId: result.assetId,
        format: result.format,
        fileSize: result.fileSize ?? archiveFile?.fileSize ?? null,
      })
    } catch {
      if (!isStale()) openStudio(null)
    }
  }, [itemSlug, openStudio])

  // Modified clicks keep the link's native behavior (new tab, new window).
  const handleClick = useCallback((event) => {
    if (!isPlainLeftClick(event)) return

    event.preventDefault()
    prepare()
  }, [prepare])

  const handleChoose = useCallback(archiveFile => prepare(archiveFile), [prepare])

  const handleCloseChoice = useCallback(() => {
    requestIdRef.current += 1
    isPreparingRef.current = false
    setIsPreparing(false)
    setCandidates(null)
  }, [])

  return {
    candidates, isPreparing, handleClick, handleChoose, handleCloseChoice,
  }
}
